import { z } from 'zod';
import { APPROVAL_INITIATOR_TYPES, APPROVAL_STATUSES, type ApprovalInitiatorType, type ApprovalStatus } from '../enums';
import { idSchema, recordLinkSchema } from './common';

/**
 * Akceptacje (MVP §10, ARCHITECTURE §8, DESIGN §5 „Akceptacja”, Figma 03): `approvals`, `approval_revisions`
 * (packages/db/src/schema/approvals.ts). Moduł uprawnień: `approvals` (typ akcji może wskazać inny moduł).
 *
 * Statusy jak w specyfikacji: `pending → approved → executing → succeeded | failed`, a także `rejected`, `expired`.
 * **Wycofanie przez moduł** (np. zestawienie wróciło do edycji, oferta zmieniona) = status `rejected` + `withdrawn_at`
 * (+ `withdrawn_reason`), bez ósmego statusu — UI pokazuje je jako „Wycofana” (`approvalDisplayStatus`).
 *
 * Zatwierdzenie jest związane z treścią: przychodzi z `payloadHash`, który osoba widziała; inny hash = CONFLICT.
 * Edycja treści = nowa rewizja (`approval_revisions`), nowy hash, poprzednia zgoda nie obowiązuje.
 * Wykonanie najwyżej raz: atomowe `UPDATE … SET status = 'executing' WHERE status = 'approved'`, licznik
 * `execution_attempts`, klucz u dostawcy `idempotency_key` (= id akceptacji). Proces zabity w `executing` → zadanie
 * uzgadniające (`last_checked_at`) woła `verify`; wynik nieznany = `failed` + `manual_check_required`.
 * Wejścia i wyjścia usług dopisuje agent modułu approvals.
 */

export { APPROVAL_INITIATOR_TYPES, APPROVAL_STATUSES, type ApprovalInitiatorType, type ApprovalStatus };
export const approvalStatusSchema = z.enum(APPROVAL_STATUSES);

/** Czekają na kogoś albo na wykonanie (zakładka „Czeka”, plakietka menu liczy tylko `pending`). */
export const OPEN_APPROVAL_STATUSES = ['pending', 'approved', 'executing'] as const satisfies readonly ApprovalStatus[];
/** Zamknięte (zakładka „Historia”). */
export const CLOSED_APPROVAL_STATUSES = [
  'rejected',
  'expired',
  'succeeded',
  'failed',
] as const satisfies readonly ApprovalStatus[];

/**
 * Dozwolone przejścia statusu (silnik akceptacji pilnuje ich warunkiem `WHERE status = …` w UPDATE).
 * `approved → rejected` = wycofanie przez moduł przed wykonaniem; `approved → failed` = wykonanie odrzucone przez
 * ponowną kontrolę (np. zatwierdzający stracił uprawnienie, rekord zmienił wersję).
 */
export const APPROVAL_STATUS_TRANSITIONS: Readonly<Record<ApprovalStatus, readonly ApprovalStatus[]>> = {
  // `pending → succeeded` = rozstrzygnięcie przez moduł (`settleApproval`, np. propozycje przyjęte w widoku spotkania)
  pending: ['approved', 'rejected', 'expired', 'succeeded'],
  // `approved → succeeded` = rozstrzygnięcie modułu sprzed silnika (status `approved` z wynikiem, bez wykonania)
  approved: ['executing', 'rejected', 'failed', 'succeeded'],
  // `executing → approved` = przejściowy błąd zgłoszony przez adapter PRZED skutkiem (`retryable`), ponowienie z tym
  // samym kluczem idempotencji; nigdy po niepewnym wyniku
  executing: ['succeeded', 'failed', 'approved'],
  rejected: [],
  expired: [],
  succeeded: [],
  failed: [],
};

export function canTransitionApproval(from: ApprovalStatus, to: ApprovalStatus): boolean {
  return APPROVAL_STATUS_TRANSITIONS[from].includes(to);
}

/** Priorytet (push dla akceptacji przy ustawieniu „tylko wysoki priorytet”, `user_settings.approval_push = 'high'`). */
export const APPROVAL_PRIORITIES = ['normal', 'high'] as const;
export type ApprovalPriority = (typeof APPROVAL_PRIORITIES)[number];
export const approvalPrioritySchema = z.enum(APPROVAL_PRIORITIES);

/** Domyślna ważność akceptacji (DECISIONS [T] 4.10: 72 h). */
export const APPROVAL_DEFAULT_TTL_HOURS = 72;
/** Po tylu sekundach w `executing` zadanie uzgadniające sprawdza wynik (`verify`), zamiast czekać w nieskończoność. */
export const APPROVAL_EXECUTION_STALE_AFTER_SEC = 10 * 60;
/** Kolejka wykonania (job z `singletonKey = approval.id`) i uzgadniania. */
export const APPROVAL_QUEUES = {
  execute: 'approvals.execute',
  reconcile: 'approvals.reconcile',
  expire: 'approvals.expire',
  /** co godzinę: wysoki priorytet dla akcji wygasających wkrótce i typów z priorytetem zależnym od czasu */
  priority: 'approvals.priority',
} as const;

/**
 * Akcja czekająca, która wygasa w ciągu tylu godzin, dostaje wysoki priorytet (push przy domyślnym „Tylko wysoki
 * priorytet”; DECISIONS Fala 3, rozstrzygnięcia). Zadanie `approvals.priority` podnosi go co godzinę.
 */
export const APPROVAL_EXPIRING_HIGH_PRIORITY_HOURS = 12;

/** Oznaczenie wyniku symulowanego (adaptery fake) — w UI i w wyniku, nigdy jako realna wysyłka. */
export const APPROVAL_SIMULATION_NOTE = 'Symulacja — nic nie zostało wysłane';
/** Baner przed wykonaniem, gdy adapter akcji jest atrapą (`willSimulate`). */
export const APPROVAL_SIMULATION_AHEAD_NOTE = 'Symulacja — nic nie zostanie wysłane';
/** Adnotacja po przerwanym wykonaniu z nieznanym wynikiem (ARCHITECTURE §8: nigdy ślepego ponowienia). */
export const APPROVAL_MANUAL_CHECK_NOTE = 'Wymaga ręcznej weryfikacji';
/** Uzgadnianie: dostawca potwierdził, że operacja się nie wykonała (ponowienie = nowa akceptacja). */
export const APPROVAL_NOT_DONE_NOTE = 'Niewykonane, można ponowić ręcznie (nowa akceptacja)';
/** Najwięcej prób wykonania po przejściowych błędach adaptera (ten sam klucz idempotencji). */
export const APPROVAL_MAX_EXECUTION_ATTEMPTS = 3;
/** Opóźnienie ponowienia po przejściowym błędzie: 60 s · 2^(próba − 1). */
export const APPROVAL_RETRY_BASE_DELAY_SEC = 60;

/**
 * Migawka uprawnionych do decyzji w chwili utworzenia (`approvals.required_flag`, `eligible_approver_ids`).
 * Służy listom („moje do decyzji”) i audytowi; przy decyzji backend i tak sprawdza świeży AccessProfile
 * (osoba, która straciła flagę, nie zatwierdzi mimo obecności w migawce; nowa osoba z flagą — może).
 * `fallback = true`: nikt nie miał wymaganej flagi, więc decyduje owner/admin.
 */
export interface ApprovalEligibility {
  requiredFlag: string;
  userIds: string[];
  fallback: boolean;
}

/** „Co się stanie” (jedna linia na karcie, DESIGN §5), zapisywane w `approvals.effects`. */
export const approvalEffectsSchema = z.object({
  /** „Wyśle 1 maila i doda zadanie „Sekcja FAQ”. Wysłanego maila nie da się cofnąć.” */
  text: z.string().min(1).max(500),
  /** rekordy, które powstaną albo się zmienią */
  links: z.array(recordLinkSchema).max(20).optional(),
});
export type ApprovalEffects = z.infer<typeof approvalEffectsSchema>;

/** Status do wyświetlenia: statusy z bazy + „Wycofana” (rejected z `withdrawn_at`). */
export const APPROVAL_DISPLAY_STATUSES = [...APPROVAL_STATUSES, 'withdrawn'] as const;
export type ApprovalDisplayStatus = (typeof APPROVAL_DISPLAY_STATUSES)[number];

export function approvalDisplayStatus(a: {
  status: ApprovalStatus | string;
  withdrawnAt?: Date | string | null;
}): ApprovalDisplayStatus {
  if (a.status === 'rejected' && a.withdrawnAt) return 'withdrawn';
  return a.status as ApprovalStatus;
}

/** Akceptacja czeka i nie wygasła (do decyzji). Wygaśnięcie w bazie ustawia zadanie `approvals.expire`. */
export function isApprovalDecidable(a: { status: string; expiresAt: Date }, now: Date): boolean {
  return a.status === 'pending' && a.expiresAt.getTime() > now.getTime();
}

/**
 * Stan akceptacji pokazywany na rekordzie, którego dotyczy (zestawienie, oferta — kompozycja Fali 3): czeka,
 * zatwierdzona / wykonywana, wykonana (z oznaczeniem symulacji), błąd, odrzucona, wycofana, wygasła. Bez treści
 * akcji (kwoty i odbiorcy są tylko w module Akceptacje).
 */
export interface ApprovalStateRef {
  id: string;
  status: ApprovalStatus;
  displayStatus: ApprovalDisplayStatus;
  /** wykonanie przez adapter fake („Symulacja — nic nie zostało wysłane”) */
  simulated: boolean;
  /** komunikat błędu wykonania (status `failed`) */
  error: string | null;
  manualCheckRequired: boolean;
}

export function approvalStateOf(row: {
  id: string;
  status: string;
  withdrawnAt?: Date | string | null;
  simulated?: boolean | null;
  error?: string | null;
  manualCheckRequired?: boolean | null;
}): ApprovalStateRef {
  return {
    id: row.id,
    status: row.status as ApprovalStatus,
    displayStatus: approvalDisplayStatus(row),
    simulated: Boolean(row.simulated),
    error: row.status === 'failed' ? (row.error ?? null) : null,
    manualCheckRequired: Boolean(row.manualCheckRequired),
  };
}

/** Zatwierdzona i czeka na wykonanie albo wykonywana (rekord odświeża się, aż pokaże wynik). */
export function isApprovalInFlight(state: { status: string } | null | undefined): boolean {
  return state?.status === 'approved' || state?.status === 'executing';
}

/** Karta akceptacji w module Akceptacje (rozwinięta; działa też dla akcji w Historii). */
export function approvalCardPath(id: string): string {
  return `/akceptacje?id=${id}`;
}

/** Lista akceptacji klienta (baner „N akcji czeka na akceptację” w profilu klienta). */
export function clientApprovalsPath(clientId: string): string {
  return `/akceptacje?klient=${clientId}`;
}

// ── Wejścia usług (router `approvals`) ──────────────────────────────────────

export const APPROVAL_LIST_TABS = ['pending', 'history'] as const;
export type ApprovalListTab = (typeof APPROVAL_LIST_TABS)[number];

export const listApprovalsInput = z
  .object({
    /** „Czeka” = pending (+ approved/executing w toku), „Historia” = zamknięte */
    tab: z.enum(APPROVAL_LIST_TABS).default('pending'),
    /** tylko te, które osoba może rozstrzygnąć (domyślnie: wszystkie widoczne) */
    decidableOnly: z.boolean().default(false),
    clientId: idSchema.optional(),
    type: z.string().min(1).max(80).optional(),
    /** rekord, którego dotyczy (karta zadania, zestawienie, oferta) */
    target: recordLinkSchema.optional(),
    limit: z.number().int().min(1).max(200).default(50),
    /** stronicowanie historii: `createdAt` + `id` ostatniej pozycji (ISO|uuid) */
    cursor: z.string().max(100).optional(),
  })
  .default({});
export type ListApprovalsInput = z.input<typeof listApprovalsInput>;

export const approvalRefInput = z.object({ id: idSchema });

/** sha256 hex kanonicznego JSON (`payloadHash` z @fos/core). */
export const payloadHashSchema = z.string().regex(/^[0-9a-f]{64}$/, { message: 'Nieprawidłowy skrót treści' });

/**
 * „Zatwierdź i wykonaj”: hash treści, którą osoba widziała (inny = CONFLICT, MVP §16 Etap 4). `confirmations` = jawne
 * potwierdzenia wymagane przez typ akcji (`ApprovalDetail.confirmations`, np. adres odbiorcy wskazany przez agenta —
 * Fala 6); brak któregoś = VALIDATION_ERROR, nic się nie zmienia.
 */
export const approveApprovalInput = z.object({
  id: idSchema,
  payloadHash: payloadHashSchema,
  note: z.string().trim().max(500).optional(),
  confirmations: z.array(z.string().trim().min(1).max(320)).max(50).default([]),
});

/**
 * Jawne potwierdzenie wymagane od zatwierdzającego (Fala 6): `key` przesyłany w `confirmations`, `label` do pola
 * wyboru („Potwierdzam odbiorcę: x@y.pl”), `reason` — ostrzeżenie („Adres spoza kontaktów klienta — wskazał agent”).
 */
export interface ApprovalConfirmation {
  key: string;
  label: string;
  reason: string;
}

export const rejectApprovalInput = z.object({
  id: idSchema,
  note: z.string().trim().max(500).optional(),
});

/**
 * „Edytuj”: nowa treść (walidowana schematem typu akcji) na bazie rewizji, którą osoba widziała. Tworzy nową rewizję
 * i nowy hash; zgoda na poprzednią treść przestaje obowiązywać. Zmienić można tylko pola dozwolone przez typ
 * (`ApprovalDetail.editableFields`), pozostałe muszą zostać bez zmian.
 */
export const editApprovalInput = z.object({
  id: idSchema,
  basePayloadHash: payloadHashSchema,
  payload: z.unknown(),
  note: z.string().trim().max(500).optional(),
});

/** Wycofanie przez moduł albo autora (status `rejected` + `withdrawn_at`, UI „Wycofana”). */
export const withdrawApprovalInput = z.object({
  id: idSchema,
  reason: z.string().trim().min(1).max(500),
});

/** Rozstrzygnięcie przez moduł (`settleApproval`), np. propozycje zadań przyjęte w widoku spotkania. */
export type ApprovalSettleOutcome =
  | { status: 'succeeded'; result?: unknown; note?: string | null }
  | { status: 'rejected'; note?: string | null };

// ── Podgląd (renderer typu) ─────────────────────────────────────────────────

export interface ApprovalFact {
  label: string;
  value: string;
}

export interface ApprovalPreviewLink {
  label: string;
  /** ścieżka SPA, np. `/spotkania/<id>` */
  path: string;
}

interface ApprovalPreviewBase {
  /** krótkie fakty pod nagłówkiem („Oferta #7 · wersja 2”, „Termin płatności 16.10.2026”) */
  facts: ApprovalFact[];
  links: ApprovalPreviewLink[];
}

export interface ApprovalEmailPreview extends ApprovalPreviewBase {
  kind: 'email';
  to: { email: string; name: string | null }[];
  subject: string;
  /** pełna treść; `null` = ukryta (zawiera kwoty, osoba bez „Widzi stawki i kwoty”) */
  body: string | null;
  attachments: { name: string; sizeBytes: number | null }[];
}

export interface ApprovalInvoicePreview extends ApprovalPreviewBase {
  kind: 'invoice';
  client: { id: string; name: string; nip: string | null };
  draft: { id: string; number: string };
  period: { from: string; to: string };
  /** bez „Widzi stawki i kwoty”: bez `unitPrice` i `amount`, opis bez stawki */
  lines: {
    description: string;
    quantity: string;
    unit: string;
    seconds: number | null;
    unitPrice?: string;
    amount?: string;
    vatRate: string;
  }[];
  totals: { net?: string; seconds: number };
  currency: string;
  dueDays: number;
}

export interface ApprovalTaskProposalsPreview extends ApprovalPreviewBase {
  kind: 'task_proposals';
  meeting: { id: string; title: string; startsAt: Date } | null;
  proposals: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    assigneeName: string | null;
    dueDate: string | null;
  }[];
}

export interface ApprovalMemoryPreview extends ApprovalPreviewBase {
  kind: 'memory';
  profileKind: string;
  profileLabel: string;
  baseVersion: number | null;
  /** diff linia po linii (`diffLines` z contracts/context); `null` = profile widzą tylko owner/admin/manager */
  diff: { op: 'same' | 'add' | 'del'; text: string }[] | null;
}

export interface ApprovalOfferTermsPreview extends ApprovalPreviewBase {
  kind: 'offer_terms';
  offer: { id: string; number: number; title: string; versionNo: number };
  /** bez „Widzi stawki i kwoty”: `before`/`after` stawki = null (same nazwy pól) */
  changes: { field: string; label: string; before: string | null; after: string | null }[];
}

export interface ApprovalDeployPreview extends ApprovalPreviewBase {
  kind: 'deploy';
  repo: string;
  commit: string;
  branch: string | null;
  environment: string;
  checks: { name: string; status: 'passed' | 'failed' | 'skipped' | 'pending' }[];
  /** „testy 12/12” */
  checksSummary: string | null;
  rollback: string | null;
  description: string | null;
  /**
   * Pytanie-nagłówek ekranu szczegółów (Figma 03 mobile: „Wdrożyć … na rea.pl?”; kompozycja Fali 5). Brak = opis
   * akcji (`summary`).
   */
  question?: string | null;
}

export interface ApprovalGenericPreview extends ApprovalPreviewBase {
  kind: 'generic';
}

export type ApprovalPreview =
  | ApprovalEmailPreview
  | ApprovalInvoicePreview
  | ApprovalTaskProposalsPreview
  | ApprovalMemoryPreview
  | ApprovalOfferTermsPreview
  | ApprovalDeployPreview
  | ApprovalGenericPreview;
export type ApprovalPreviewKind = ApprovalPreview['kind'];

// ── Wyjścia (DTO) ───────────────────────────────────────────────────────────

export interface ApprovalActorRef {
  type: ApprovalInitiatorType;
  userId: string | null;
  /** „Seban”, „Copilot”, „Worker”, „Claude Code (MCP)” */
  label: string;
}

export interface ApprovalListItem {
  id: string;
  type: string;
  /** etykieta typu: „Email”, „Faktura”, „Deploy”, „Zadania” */
  typeLabel: string;
  /** rodzaj podglądu (ikona typu na karcie) */
  previewKind: ApprovalPreviewKind;
  status: ApprovalStatus;
  /** status do wyświetlenia: + „Wycofana”; czekająca po terminie = `expired` (zanim zadanie ją zamknie) */
  displayStatus: ApprovalDisplayStatus;
  priority: ApprovalPriority;
  /** opis dla osoby (bez kwot bez „Widzi stawki i kwoty”: `approvalSummaryFor`) */
  summary: string;
  /** początek treści (mail) do zwiniętej karty na mobile; `null` = brak albo ukryta */
  excerpt: string | null;
  /** dodatkowa meta („testy 12/12”, „3 propozycje”) */
  hint: string | null;
  client: { id: string; name: string } | null;
  initiator: ApprovalActorRef;
  target: { type: string; id: string } | null;
  /** rekord, na którym akcję widać i rozstrzyga się ją w kontekście (spotkanie, zestawienie, oferta) */
  reviewPath: string | null;
  revision: number;
  irreversible: boolean;
  simulated: boolean;
  expiresAt: Date;
  createdAt: Date;
  decidedAt: Date | null;
  executedAt: Date | null;
  /** osoba może teraz zatwierdzić (świeży AccessProfile, status `pending`, nie wygasła) */
  canDecide: boolean;
  /** osoba może zmienić treść (typ ma pola do edycji, treść widoczna) */
  canEdit: boolean;
  manualCheckRequired: boolean;
}

export interface ApprovalRevisionDto {
  revision: number;
  payloadHash: string;
  summary: string | null;
  note: string | null;
  editedBy: { userId: string; name: string } | null;
  createdAt: Date;
}

export interface ApprovalDetail extends ApprovalListItem {
  /**
   * Pełna treść bieżącej rewizji (do edycji) — tylko gdy podgląd niczego nie ukrywa (`contentHidden = false`);
   * inaczej `null` (kwoty tylko z „Widzi stawki i kwoty”).
   */
  payload: unknown;
  payloadHash: string;
  preview: ApprovalPreview;
  /** podgląd ukrywa kwoty (osoba bez „Widzi stawki i kwoty”) — takiej treści nie zatwierdza ani nie edytuje */
  contentHidden: boolean;
  /** pola payloadu, które wolno zmienić w „Edytuj” (puste = bez edycji) */
  editableFields: string[];
  recipients: string[];
  effects: ApprovalEffects | null;
  /** adapter akcji jest atrapą: wykonanie będzie symulacją (baner przed zatwierdzeniem) */
  willSimulate: boolean;
  /** rekord, którego dotyczy akcja, zmienił się od przygotowania (zatwierdzenie = CONFLICT) */
  targetStale: boolean;
  /** dlaczego osoba nie może zatwierdzić (null = może albo akcja nie czeka) */
  decideBlockedReason: string | null;
  /**
   * Potwierdzenia wymagane przed zatwierdzeniem (akcja czeka): zatwierdzenie bez nich = VALIDATION_ERROR. Pusta lista
   * = bez dodatkowych potwierdzeń (Fala 6: odbiorca maila spoza kontaktów klienta wskazany przez agenta).
   */
  confirmations: ApprovalConfirmation[];
  decidedBy: { userId: string; name: string } | null;
  decisionNote: string | null;
  withdrawnAt: Date | null;
  withdrawnReason: string | null;
  executionStartedAt: Date | null;
  executionAttempts: number;
  lastCheckedAt: Date | null;
  result: unknown;
  error: string | null;
  revisions: ApprovalRevisionDto[];
}

export interface ApprovalListPage {
  items: ApprovalListItem[];
  /** następna strona (Historia) */
  nextCursor: string | null;
}

export interface ApprovalCounts {
  /** czekające, które osoba może zatwierdzić (plakietka „Akceptacje”, niebieska) */
  decidable: number;
  /** wszystkie czekające w zakresie osoby (licznik „Czeka · N”) */
  pending: number;
}

export interface ApprovalStats {
  /** wczoraj zatwierdzone / odrzucone (stopka Figmy 03: „Wczoraj zatwierdzono 4 działania, odrzucono 1”) */
  yesterday: { approved: number; rejected: number };
}

/** Wynik zadania `approvals.execute` (worker, testy). */
export type ApprovalExecutionOutcome =
  | { status: 'succeeded'; simulated: boolean; result: unknown }
  | { status: 'failed'; error: string }
  | { status: 'retry'; error: string; attempt: number }
  | { status: 'skipped'; reason: string };

export const APPROVAL_MESSAGES = {
  notFound: 'Nie znaleziono akceptacji',
  notPending: 'Ta akcja nie czeka już na decyzję',
  expired: 'Akceptacja wygasła. Przygotuj akcję jeszcze raz.',
  staleHash: 'Treść zmieniła się — sprawdź ponownie',
  targetChanged: 'Dane się zmieniły od przygotowania akcji',
  notEligible: 'Nie możesz zatwierdzić tej akcji',
  viewOnly: 'Masz tylko podgląd modułu Akceptacje',
  aiCannotDecide: 'AI i MCP nie zatwierdzają akcji',
  confirmationRequired: 'Potwierdź odbiorcę wskazanego przez agenta, zanim zatwierdzisz',
  confirmationRevisionNote: 'Odbiorca potwierdzony przy zatwierdzeniu',
  contentHidden: 'Treść zawiera kwoty. Zatwierdza ją osoba z uprawnieniem „Widzi stawki i kwoty”.',
  alreadyExecuted: 'Ta akcja została już wykonana',
  notEditable: 'Tej akcji nie można edytować',
  fieldNotEditable: 'Tego pola nie można zmienić',
  noChange: 'Treść się nie zmieniła',
  cannotWithdraw: 'Tej akcji nie można już wycofać',
  executing: 'Akcja jest właśnie wykonywana',
  deciderLostAccess: 'Osoba, która zatwierdziła, nie ma już uprawnień do tej akcji',
  executionFailed: 'Nie udało się wykonać akcji',
  manualCheck: APPROVAL_MANUAL_CHECK_NOTE,
  notDone: APPROVAL_NOT_DONE_NOTE,
  simulated: APPROVAL_SIMULATION_NOTE,
  unknownType: 'Nieznany typ akcji',
  /** decyzja zapisana razem z prośbą: osoba w aplikacji z prawem do akcji kliknęła „Wyślij” / „Wystaw fakturę” */
  approvedAtRequest: 'Zatwierdzone przy wysyłce',
} as const;

/**
 * Akcje „Wymaga ręcznej weryfikacji” bez rekordu źródłowego, który by je rozstrzygał (np. przypomnienie, wdrożenie),
 * zostają w zakładce „Czeka” przez tyle dni od wyniku. Mail, faktura i oferta — dopóki szkic / zestawienie / wersja
 * oferty wskazuje tę akceptację (po „Sprawdziłem…”, „Oznacz jako wystawione” albo ponownej wysyłce znikają).
 */
export const APPROVAL_MANUAL_CHECK_PENDING_DAYS = 7;
